import { useMutation, useQueryClient } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

export interface CreateEmployeeRequest {
  firstName: string
  surname: string
  email: string
  functionAreas: string[]
  location: string[]
  driverManagerTeam?: string
}

export interface CreateEmployeeResponse {
  success: boolean
  person: {
    id: string
    personId: string
    person: string
    userId: string
    firstName: string
    lastName: string
    email: string
    employer: string
    areas: string[]
    location: string[]
    driverManagerTeam: string | null
  }
  error?: string
}

/**
 * Create a HIVED employee: a person record backed by a silent auth user (no
 * DriverHub access). Calls the people edge function's /create-employee route.
 */
export function useCreateEmployee() {
  const queryClient = useQueryClient()

  return useMutation<CreateEmployeeResponse, Error, CreateEmployeeRequest>({
    mutationFn: async (request) => {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      if (!token) {
        throw new Error("No authentication token available")
      }

      const functionUrl = `${getFunctionsBaseUrl()}/people/create-employee`

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to create employee: ${response.statusText}`)
      }

      const data: CreateEmployeeResponse = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to create employee")
      }

      return data
    },
    onSuccess: () => {
      // Refresh the people directory and users list.
      queryClient.invalidateQueries({ queryKey: ["people"] })
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}
